import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import type { ListResult, MarketplaceListingView, MarketplaceOrderView } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { formatBRL } from "@cx/domain";
import { ContentHeader, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import { PROVIDER_UI, orderStatusLabel, reviewLabel } from "./labels";
import { useMarketplaceAccounts } from "./useAccounts";

/** Pedidos importados dos marketplaces: pedido de venda gerado, revisão, reprocessar e reaplicar vínculos. */
export function MarketplaceOrdersPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canUpdate = can(perms, "marketplaces", "update");
  const canSeeOrders = can(perms, "sales_orders", "read");
  const search = useSearch({ from: "/vendas/marketplace/pedidos" });
  const navigate = useNavigate({ from: "/vendas/marketplace/pedidos" });
  const toast = useToast();
  const qc = useQueryClient();
  const accounts = useMarketplaceAccounts();
  const list = accounts.data?.accounts ?? [];
  const modes = accounts.data?.modes;
  const providerOf = new Map(list.map((a) => [a.id, a.provider]));
  const sandboxAccount = list.find(
    (a) =>
      a.sandbox &&
      a.status === "active" &&
      modes?.[a.provider] === "sandbox" &&
      (!search.conta || a.id === search.conta),
  );
  const [review, setReview] = useState<"" | "true" | "false">("");
  const [simulate, setSimulate] = useState(false);
  const orders = useInfiniteQuery({
    queryKey: ["marketplace-orders", companyId, search.conta, review],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api<ListResult<MarketplaceOrderView>>(
        `/marketplaces/orders?${new URLSearchParams({
          ...(search.conta ? { accountId: search.conta } : {}),
          ...(review ? { review } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (p) => p.nextCursor,
  });
  const rows = orders.data?.pages.flatMap((p) => p.data) ?? [];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["marketplace-orders", companyId] });
    void qc.invalidateQueries({ queryKey: ["marketplace-accounts", companyId] });
  };
  const act = useMutation({
    mutationFn: ({ id, what }: { id: string; what: "reprocess" | "relink" }) =>
      api<unknown>(`/marketplaces/orders/${id}/${what}`, {
        method: "POST",
        headers: what === "relink" ? { "Idempotency-Key": crypto.randomUUID() } : {},
      }),
    onSuccess: (_r, { what }) => {
      toast("success", what === "relink" ? "Vínculos reaplicados" : "Pedido reprocessado");
      refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <>
      <ContentHeader
        title="Pedidos do marketplace"
        breadcrumb={["Vendas", "Pedidos do marketplace"]}
        actions={
          canUpdate && sandboxAccount ? (
            <button type="button" className="btn btn-outline-secondary" onClick={() => setSimulate(true)}>
              Simular pedido (testes)
            </button>
          ) : undefined
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0 align-items-end">
          <Field label="Conta" className="col-md-4">
            <select
              className="form-select"
              value={search.conta ?? ""}
              onChange={(e) => navigate({ search: { conta: e.target.value || undefined }, replace: true })}
            >
              <option value="">Todas</option>
              {list.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.storeName} ({PROVIDER_UI[a.provider].label})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Revisão" className="col-md-4">
            <select
              className="form-select"
              value={review}
              onChange={(e) => setReview(e.target.value as "" | "true" | "false")}
            >
              <option value="">Todos</option>
              <option value="true">Para revisar</option>
              <option value="false">Sem pendência</option>
            </select>
          </Field>
          <div className="col-md-4 mb-3">
            <button type="button" className="btn btn-outline-secondary" onClick={refresh}>
              Atualizar
            </button>
          </div>
        </div>
        <QueryStatus loading={orders.isLoading} error={orders.error} />
        {orders.isSuccess && rows.length === 0 && (
          <p className="text-muted p-3 mb-0">Nenhum pedido importado.</p>
        )}
        {rows.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Pedidos do marketplace">
              <thead>
                <tr>
                  <th scope="col">Pedido</th>
                  <th scope="col">Data</th>
                  <th scope="col">Loja</th>
                  <th scope="col">Situação no marketplace</th>
                  <th scope="col">Itens</th>
                  <th scope="col" className="text-end">
                    Total
                  </th>
                  <th scope="col">Pedido de venda</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const provider = providerOf.get(o.accountId) ?? "mercadolivre";
                  const why = reviewLabel(o.reviewReason, provider);
                  return (
                    <tr key={o.id}>
                      <td>
                        {o.externalOrderId}
                        {o.packId && <div className="small">Pacote {o.packId}</div>}
                      </td>
                      <td>{dateTime(o.dateCreated)}</td>
                      <td>
                        {o.storeName}
                        <div className="small">{PROVIDER_UI[provider].label}</div>
                      </td>
                      <td>
                        {orderStatusLabel(o.status, provider)}
                        {why && (
                          <div>
                            <StatusBadge tone="warning">Revisar</StatusBadge>{" "}
                            <span className="small">{why}</span>
                          </div>
                        )}
                      </td>
                      <td className="small">
                        {o.items.map((i) => (
                          <div key={`${i.itemId}-${i.variationId ?? ""}`}>
                            {i.qty} × {i.title}
                            {!i.productId && <span className="text-danger"> (sem vínculo)</span>}
                          </div>
                        ))}
                      </td>
                      <td className="text-end">{formatBRL(o.totalCents)}</td>
                      <td>
                        {o.salesOrder ? (
                          canSeeOrders ? (
                            <Link to="/vendas/pedidos/$id" params={{ id: o.salesOrder.id }}>
                              Nº {o.salesOrder.number}
                            </Link>
                          ) : (
                            `Nº ${o.salesOrder.number}`
                          )
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="text-end text-nowrap">
                        {canUpdate && (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary me-1"
                              disabled={act.isPending}
                              onClick={() => act.mutate({ id: o.id, what: "reprocess" })}
                            >
                              Reprocessar
                            </button>
                            {o.salesOrder && o.reviewReason === "unlinked_items" && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                disabled={act.isPending}
                                onClick={() => act.mutate({ id: o.id, what: "relink" })}
                              >
                                Reaplicar vínculos
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {orders.hasNextPage && (
          <div className="p-3">
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={orders.isFetchingNextPage}
              onClick={() => void orders.fetchNextPage()}
            >
              Carregar mais
            </button>
          </div>
        )}
      </div>
      {simulate && sandboxAccount && (
        <SimulateDialog
          accountId={sandboxAccount.id}
          marketplace={PROVIDER_UI[sandboxAccount.provider].label}
          onClose={() => setSimulate(false)}
          onDone={() => {
            setSimulate(false);
            toast("success", "Pedido simulado enviado. Ele aparece aqui em instantes.");
            window.setTimeout(refresh, 1500);
          }}
        />
      )}
    </>
  );
}

/** Pedido no marketplace de testes: anúncio, quantidade e situação. */
function SimulateDialog({
  accountId,
  marketplace,
  onClose,
  onDone,
}: {
  accountId: string;
  marketplace: string;
  onClose(): void;
  onDone(): void;
}) {
  const { companyId, api } = useSession();
  const toast = useToast();
  const listings = useQuery({
    queryKey: ["marketplace-listings", companyId, accountId, "simulate"],
    queryFn: () =>
      api<ListResult<MarketplaceListingView>>(`/marketplaces/accounts/${accountId}/listings?limit=100`),
  });
  const [listingId, setListingId] = useState("");
  const [qty, setQty] = useState(1);
  const [status, setStatus] = useState<"paid" | "payment_required">("paid");
  const send = useMutation({
    mutationFn: () =>
      api<{ orderId: string }>(`/marketplaces/accounts/${accountId}/sandbox/orders`, {
        method: "POST",
        body: JSON.stringify({ ...(listingId ? { listingId } : {}), qty, status }),
      }),
    onSuccess: onDone,
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open
      title={`Simular pedido: ${marketplace} de testes`}
      onClose={onClose}
      footer={
        <button
          type="button"
          className="btn btn-primary"
          disabled={send.isPending}
          onClick={() => send.mutate()}
        >
          Enviar pedido
        </button>
      }
    >
      <Field label="Anúncio">
        <select
          className="form-select"
          value={listingId}
          onChange={(e) => setListingId(e.target.value)}
          data-autofocus
        >
          <option value="">Primeiro anúncio</option>
          {(listings.data?.data ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.title}
              {l.variationLabel ? ` · ${l.variationLabel}` : ""}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Quantidade">
        <input
          type="number"
          min={1}
          max={10}
          value={qty}
          onChange={(e) => setQty(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
        />
      </Field>
      <Field label="Situação">
        <select
          className="form-select"
          value={status}
          onChange={(e) => setStatus(e.target.value as "paid" | "payment_required")}
        >
          <option value="paid">Pago</option>
          <option value="payment_required">Aguardando pagamento</option>
        </select>
      </Field>
    </Modal>
  );
}
